import React, { FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './AreaStep.scss'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { RealEstateType } from '../../@types/realEstateType'
import { useReportViewEvent } from '../../hooks'
import { Progress } from '../../@types/progress'
import { reportTrackingEvent } from '../../tracking/thunks'
import { AreaStepIcon } from '../../components/icons/StepIcons/AreaStepIcon'
import { ReduxState } from '../../store/reducers'
import Area from '../../components/Area/Area'
import { validateLivingSpace, validatePlotArea } from '../../components/Area/utils'

const headLineMap = {
  [RealEstateType.HOUSE]: 'Wie groß sind Wohn- und Grundstücksfläche?',
  [RealEstateType.APARTMENT]: 'Wie groß ist die Wohnfläche?',
  [RealEstateType.LAND]: 'Wie groß ist das Grundstück?',
  [RealEstateType.COMMERCIAL]: 'Wie groß ist die Gewerbefläche?',
}

const pageTitleMap = {
  [RealEstateType.HOUSE]: 'housespace',
  [RealEstateType.APARTMENT]: 'apartmentspace',
  [RealEstateType.LAND]: 'propertyspace',
  [RealEstateType.COMMERCIAL]: 'commercialspace',
}

interface AreaStepProps {
  goToNextStep?: () => unknown
  goToPreviousStep?: () => unknown
  progress: Progress
  displayBack: boolean
}

export const AreaStep: FC<AreaStepProps> = ({ ...props }) => {
  const {
    config: { theme },
    lead: { realEstateType: stateRealEstateType, livingSpace, plotArea },
    area: { propertyChecked },
  } = useSelector((state: ReduxState) => state)
  const realEstateType = stateRealEstateType ? stateRealEstateType : RealEstateType.HOUSE
  useReportViewEvent({ pageTitle: pageTitleMap[realEstateType] })

  const [livingSpaceInputType, setLivingSpaceInputType] = useState<string>('default')
  const [plotAreaInputType, setPlotAreaInputType] = useState<string>('default')

  const dispatch = useDispatch()

  const isValidLivingSpace = realEstateType === RealEstateType.LAND || validateLivingSpace(realEstateType, livingSpace)
  const plotAreaDisabled = realEstateType === RealEstateType.COMMERCIAL && !propertyChecked
  const isValidPlotArea =
    realEstateType === RealEstateType.APARTMENT || plotAreaDisabled || validatePlotArea(realEstateType, plotArea)

  const isNextEnabled = isValidLivingSpace && isValidPlotArea

  const onNextClick = () => {
    const inputValue = `${livingSpace || ''}${livingSpace && plotArea ? '_' : ''}${plotArea || ''}`

    let eventLabel = `${pageTitleMap[realEstateType]}`

    if (livingSpace) {
      eventLabel = eventLabel.concat(`_${livingSpaceInputType}`)
    }

    if (plotArea) {
      eventLabel = eventLabel.concat(`_${plotAreaInputType}`)
    }

    dispatch(
      reportTrackingEvent({
        pageTitle: pageTitleMap[realEstateType],
        label: eventLabel,
        inputValue: inputValue,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: inputValue,
        },
      })
    )

    if (props.goToNextStep) {
      props.goToNextStep()
    }
  }

  return (
    <StepTemplate
      headline={headLineMap[realEstateType]}
      icon={<AreaStepIcon />}
      isNextEnabled={isNextEnabled}
      goToNextStep={onNextClick}
      goToPreviousStep={props.goToPreviousStep}
      progress={props.progress}
      dataTestingButton='area-next-button'
      displayBack={props.displayBack}
      backgroundColor={theme.background}
      textColor={theme.text}
    >
      <Area
        onLivingSpaceChanged={() => setLivingSpaceInputType('submit')}
        onPlotAreaChanged={() => setPlotAreaInputType('submit')}
        className={'area-container'}
      />
    </StepTemplate>
  )
}

export default AreaStep
